import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { App } from '@capacitor/app'
import { CapacitorUtils } from 'capacitor-utils'
import { useAppStore } from '@/stores/useAppStore'

const EDGES = ['top', 'right', 'bottom', 'left'] as const

/**
 * The page canvas (--ui-bg-muted, the background every routed screen paints)
 * in the palette <html> carries right now, as #rrggbb. A 1px canvas turns any
 * CSS colour syntax (oklch included) into sRGB bytes.
 */
function pageCanvasColor(): string | null {
  const probe = document.createElement('div')
  probe.style.cssText = 'position:fixed;width:0;height:0;visibility:hidden;background:var(--ui-bg-muted)'
  document.body.appendChild(probe)
  const color = getComputedStyle(probe).backgroundColor
  probe.remove()

  const context = document.createElement('canvas').getContext('2d')
  if (!context) return null
  context.fillStyle = color
  context.fillRect(0, 0, 1, 1)
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data
  return '#' + [r, g, b].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Native system-bar glue:
 *  - status-bar icon contrast tracks the light/dark background
 *  - the native layer keeps the web view clear of the system bars, the display
 *    cutout and the keyboard (WebViewInsets on Android, WebViewHostViewController
 *    on iOS), so the web layer pads for nothing there; this paints the strips
 *    around the web view in the page canvas colour.
 * No-op on web/desktop, where the CSS env() safe-area fallbacks apply.
 */
export function useSystemBars() {
  const store = useAppStore()
  const route = useRoute()
  const native = Capacitor.isNativePlatform()
  const android = Capacitor.getPlatform() === 'android'

  // A screen needs light status-bar icons when its background is dark — either
  // the app's dark theme is active, or the current route is always dark
  // regardless of theme (auth/onboarding screens flagged with darkStatusBar).
  const isDarkBackground = computed(
    () => route.meta.darkStatusBar === true || store.isDark
  )

  const applyStatusBarStyle = async (): Promise<void> => {
    if (!native) return
    try {
      // Style.Dark = light text/icons (for a dark header); Style.Light = dark text/icons (for a light header).
      if (android) {
        // Android must set the style through the core SystemBars plugin, not
        // @capacitor/status-bar: SystemBars re-applies its own stored style on
        // every configuration change (rotation, OS theme, locale, density)
        // AFTER the StatusBar plugin's, so a style set only via StatusBar gets
        // reset to the OS default on the next such change.
        await SystemBars.setStyle({
          style: isDarkBackground.value ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
          bar: SystemBarType.StatusBar
        })
      } else {
        await StatusBar.setStyle({ style: isDarkBackground.value ? Style.Dark : Style.Light })
      }
    } catch {
      // best-effort; ignore if the status bar is unavailable
    }
  }

  // The web view already sits clear of the bars, so every web inset is 0 —
  // whatever a web view version reports through env(safe-area-inset-*).
  const zeroWebInsets = (): void => {
    for (const edge of EDGES) {
      document.documentElement.style.setProperty(`--app-safe-area-${edge}`, '0px')
    }
  }

  // The strips around the web view show the page canvas, as the web view
  // itself did when it drew under the bars.
  const applyBarStripColor = async (): Promise<void> => {
    if (!native) return
    const color = pageCanvasColor()
    if (!color) return
    try {
      await CapacitorUtils.setWebViewHostBackground({ color })
    } catch {
      // best-effort; the strips keep their previous colour
    }
  }

  // TEMPORARY, read-only diagnostic for the Android system-bar spacing bug.
  // Logs the raw native insets + WebView geometry alongside the resolved CSS
  // values (one '[InsetDiag]' line, captured to the retrievable native log via
  // the console override). With the native padding in place, webViewTopPx
  // equals barsTopPx: the WebView starts below the status bar. Changes nothing
  // visible — remove once affected devices confirm it.
  const logInsetDiagnostics = async (): Promise<void> => {
    if (!android) return
    try {
      const [geometry, pluginInsets] = await Promise.all([
        CapacitorUtils.getInsetDiagnostics(),
        CapacitorUtils.getSafeAreaInsets(),
      ])
      const cs = getComputedStyle(document.documentElement)
      const tabBar = document.querySelector('[data-bottom-bar]')
      console.info('[InsetDiag]', JSON.stringify({
        native: geometry,
        pluginInsets,
        cssSafeAreaTop: cs.getPropertyValue('--app-safe-area-top').trim(),
        cssSafeAreaBottom: cs.getPropertyValue('--app-safe-area-bottom').trim(),
        tabBarPaddingBottom: tabBar ? getComputedStyle(tabBar).paddingBottom : null,
        innerHeight: window.innerHeight,
        dpr: window.devicePixelRatio,
      }))
    } catch (e) {
      console.info('[InsetDiag] failed', String(e))
    }
  }

  let diagTimer: ReturnType<typeof setTimeout> | undefined
  const scheduleInsetDiagnostics = (): void => {
    if (!android) return
    clearTimeout(diagTimer)
    // Delay so the WebView/tab bar have laid out before we read geometry.
    diagTimer = setTimeout(() => { void logInsetDiagnostics() }, 1500)
  }

  let resumeHandle: PluginListenerHandle | undefined
  let unmounted = false

  onMounted(async () => {
    if (!native) return
    zeroWebInsets()
    void applyStatusBarStyle()
    void applyBarStripColor()
    scheduleInsetDiagnostics()
    // Deliberate second 'resume' listener (App.vue registers one for
    // websocket/LAN reload). Guard against unmount before the handle resolves.
    const handle = await App.addListener('resume', () => {
      void applyStatusBarStyle()
      scheduleInsetDiagnostics()
    })
    if (unmounted) handle.remove()
    else resumeHandle = handle
  })

  onUnmounted(() => {
    unmounted = true
    clearTimeout(diagTimer)
    resumeHandle?.remove()
  })

  // Re-apply icon contrast whenever the effective background changes —
  // theme toggle or navigation to/from an always-dark screen.
  watch(isDarkBackground, applyStatusBarStyle)
  // The strip colour is read from <html>, whose dark class App.vue's theme
  // watcher sets — read it after that has run.
  watch(isDarkBackground, applyBarStripColor, { flush: 'post' })
}
